/**
 * Locate a model-supplied quote inside a Word paragraph (spec §5.1). Exact port of `app/word_kit/anchor.py`.
 *
 * Ladder: `exact` -> `normalized` -> `case_insensitive`. The first level that finds anything decides: one
 * occurrence is `ok`, two or more is `ambiguous`, and the ladder never falls through (a looser level can
 * only find more). Occurrences are counted overlapping and capped at `MATCH_COUNT_CAP`. Nothing is guessed.
 *
 * Offsets are UTF-16 code-unit indices into the ORIGINAL paragraph text (Python reports code points).
 */

import { type Collapsed, casefoldChars, isPyBlank, normalizeForMatch, sourceSpan } from "./normalize";
import { type SnapshotParagraph, type WordSnapshot, deletedSpans } from "./snapshot";

export type AnchorStatus = "ok" | "not_found" | "ambiguous";
export type AnchorLevel = "exact" | "normalized" | "case_insensitive";
export type TextBasis = "live" | "reviewed";

export const LEVELS: readonly AnchorLevel[] = ["exact", "normalized", "case_insensitive"];
/** Word's `search()` rejects search strings longer than 255 characters (UTF-16 code units). */
export const WORD_SEARCH_LIMIT = 255;
/** Counting stops here; `matchCount === MATCH_COUNT_CAP` means "at least this many". */
export const MATCH_COUNT_CAP = 20;

export interface AnchorResult {
  status: AnchorStatus;
  level: AnchorLevel | null;
  /** UTF-16 offset into the original paragraph (`ok` only). */
  start: number | null;
  end: number | null;
  matchCount: number;
}

interface PreparedQuote {
  exact: string;
  normalized: string;
  folded: string;
}

function prepare(quote: string): PreparedQuote {
  const normalized = normalizeForMatch(quote).text;
  return { exact: quote, normalized, folded: casefoldChars(normalized) };
}

function findAll(haystack: string, needle: string, cap: number): number[] {
  const found: number[] = [];
  let index = haystack.indexOf(needle);
  while (index !== -1 && found.length < cap) {
    found.push(index);
    index = haystack.indexOf(needle, index + 1);
  }
  return found;
}

/** Lazily normalised views of one paragraph text. */
export class PreparedText {
  private collapsedCache: Collapsed | null = null;
  private foldedCache: string | null = null;

  constructor(readonly text: string) {}

  collapsed(): Collapsed {
    if (this.collapsedCache === null) this.collapsedCache = normalizeForMatch(this.text);
    return this.collapsedCache;
  }

  folded(): string {
    if (this.foldedCache === null) this.foldedCache = casefoldChars(this.collapsed().text);
    return this.foldedCache;
  }
}

function spansAt(text: PreparedText, quote: PreparedQuote, level: AnchorLevel, cap: number): [number, number][] {
  if (level === "exact") {
    return findAll(text.text, quote.exact, cap).map((start): [number, number] => [start, start + quote.exact.length]);
  }
  const needle = level === "normalized" ? quote.normalized : quote.folded;
  if (!needle) return [];
  const haystack = level === "normalized" ? text.collapsed().text : text.folded();
  const collapsed = text.collapsed();
  return findAll(haystack, needle, cap).map((start) => sourceSpan(collapsed, start, start + needle.length));
}

function isBlank(quote: string): boolean {
  return isPyBlank(normalizeForMatch(quote).text);
}

const NOT_FOUND: AnchorResult = { status: "not_found", level: null, start: null, end: null, matchCount: 0 };

export type Spans = readonly (readonly [number, number])[];

/** True when `[start, end)` shares a character with a deleted span (touching is not overlap). */
export function overlapsDeleted(start: number, end: number, deleted: Spans): boolean {
  return deleted.some(([a, b]) => a < end && start < b);
}

/**
 * The occurrences that decide: those clear of deleted text when any is; else all of them. Word on the web
 * keeps tracked-deleted text in the paragraph text; the model means the text that stays. Mirrors Python.
 */
function preferUndeleted(spans: [number, number][], deleted: Spans): [number, number][] {
  if (deleted.length === 0) return spans;
  const clean = spans.filter(([a, b]) => !overlapsDeleted(a, b, deleted));
  return clean.length > 0 ? clean : spans;
}

/**
 * Find `quote` in `paragraphText` by the exact -> normalized -> case-insensitive ladder. `deleted`: UTF-16
 * spans of the text pending deletions still hold (`deletedSpans`); occurrences clear of them decide first.
 */
export function locate(paragraphText: string, quote: string, deleted: Spans = []): AnchorResult {
  return locatePrepared(new PreparedText(paragraphText), quote, deleted);
}

function locatePrepared(preparedText: PreparedText, quote: string, deleted: Spans = []): AnchorResult {
  if (isBlank(quote)) return NOT_FOUND;
  const preparedQuote = prepare(quote);
  for (const level of LEVELS) {
    const spans = preferUndeleted(spansAt(preparedText, preparedQuote, level, MATCH_COUNT_CAP), deleted);
    if (spans.length === 1) {
      const [start, end] = spans[0] as [number, number];
      return { status: "ok", level, start, end, matchCount: 1 };
    }
    if (spans.length > 0) return { status: "ambiguous", level, start: null, end: null, matchCount: spans.length };
  }
  return NOT_FOUND;
}

export interface SnapshotAnchor {
  requestedPid: string;
  /** The paragraph the quote was anchored in (`ok`), else the requested pid if it exists, else null. */
  pid: string | null;
  resolution: "pid" | "search";
  basis: TextBasis | null;
  result: AnchorResult;
  candidatePids: string[];
}

function textFor(paragraph: SnapshotParagraph, basis: TextBasis): string {
  return basis === "live" ? paragraph.text_live : paragraph.text_reviewed;
}

/** Reuses each paragraph's normalised text across many `locateInSnapshot` calls on one snapshot. */
export class AnchorCache {
  private readonly texts = new Map<string, PreparedText>();

  text(paragraph: SnapshotParagraph, basis: TextBasis): PreparedText {
    const key = `${basis}\u0000${paragraph.pid}`;
    const raw = textFor(paragraph, basis);
    let prepared = this.texts.get(key);
    if (prepared === undefined || prepared.text !== raw) {
      prepared = new PreparedText(raw);
      this.texts.set(key, prepared);
    }
    return prepared;
  }
}

function deletedFor(paragraph: SnapshotParagraph, basis: TextBasis): Spans {
  return basis === "live" ? deletedSpans(paragraph.live_segments) : [];
}

function searchAll(
  paragraphs: readonly SnapshotParagraph[],
  quote: PreparedQuote,
  basis: TextBasis,
  cache: AnchorCache,
): { result: AnchorResult; pid: string | null; candidates: string[] } | null {
  const prepared = paragraphs.map((p): [string, PreparedText, Spans] => [p.pid, cache.text(p, basis), deletedFor(p, basis)]);
  for (const level of LEVELS) {
    const found: [string, [number, number], boolean][] = [];
    for (const [pid, text, deleted] of prepared) {
      for (const span of spansAt(text, quote, level, MATCH_COUNT_CAP - found.length)) {
        found.push([pid, span, overlapsDeleted(span[0], span[1], deleted)]);
      }
      if (found.length >= MATCH_COUNT_CAP) break;
    }
    const kept = found.filter(([, , dirty]) => !dirty);
    const hits = (kept.length > 0 ? kept : found).map(([pid, span]): [string, [number, number]] => [pid, span]);
    if (hits.length === 1) {
      const [pid, [start, end]] = hits[0] as [string, [number, number]];
      return { result: { status: "ok", level, start, end, matchCount: 1 }, pid, candidates: [] };
    }
    if (hits.length > 0) {
      const candidates = [...new Set(hits.map(([pid]) => pid))];
      return {
        result: { status: "ambiguous", level, start: null, end: null, matchCount: hits.length },
        pid: null,
        candidates,
      };
    }
  }
  return null;
}

/**
 * Anchor `quote` in paragraph `pid`; if that pid does not exist, search the whole snapshot. Live text first,
 * then (only if nothing at all was found) the reviewed text.
 */
export function locateInSnapshot(
  snapshot: Pick<WordSnapshot, "paragraphs">,
  pid: string,
  quote: string,
  cache: AnchorCache = new AnchorCache(),
): SnapshotAnchor {
  const paragraph = snapshot.paragraphs.find((p) => p.pid === pid);
  if (paragraph !== undefined) {
    for (const basis of ["live", "reviewed"] as const) {
      if (basis === "reviewed" && paragraph.text_reviewed === paragraph.text_live) break;
      const result = locatePrepared(cache.text(paragraph, basis), quote, deletedFor(paragraph, basis));
      if (result.status !== "not_found") {
        return { requestedPid: pid, pid, resolution: "pid", basis, result, candidatePids: [] };
      }
    }
    return { requestedPid: pid, pid, resolution: "pid", basis: null, result: NOT_FOUND, candidatePids: [] };
  }
  if (!isBlank(quote)) {
    const preparedQuote = prepare(quote);
    for (const basis of ["live", "reviewed"] as const) {
      const found = searchAll(snapshot.paragraphs, preparedQuote, basis, cache);
      if (found !== null) {
        return {
          requestedPid: pid,
          pid: found.pid,
          resolution: "search",
          basis,
          result: found.result,
          candidatePids: found.candidates,
        };
      }
    }
  }
  return { requestedPid: pid, pid: null, resolution: "search", basis: null, result: NOT_FOUND, candidatePids: [] };
}

/** What the pane passes to Word's `search()` to find a span (spec §5.1 step 3). */
export interface SearchProbes {
  /** Escaped for Word search (`^` -> `^^`): the whole span if it fits, else its longest prefix that does. */
  head: string;
  /** Null when `head` is the whole span; else the longest escaped suffix that fits. */
  tail: string | null;
  /** The unescaped text of `head` / `tail` (what the probe matches in the document). */
  headRaw: string;
  tailRaw: string | null;
}

/** Word treats `^` as the start of a special code (`^p`, `^t`, `^?` …) in non-wildcard searches. */
export function escapeWordSearch(text: string): string {
  return text.replace(/\^/g, "^^");
}

/** Code points of `text`, each paired with its escaped UTF-16 width. */
function escapedUnits(text: string): { raw: string; width: number }[] {
  return [...text].map((raw) => ({ raw, width: raw === "^" ? 2 : raw.length }));
}

/**
 * Split `spanText` into Word search probes. Lengths are measured in UTF-16 units AFTER escaping `^`, and
 * neither a surrogate pair nor an escaped `^^` is ever split. Without `^` this equals Python's `search_probes`.
 */
export function searchProbes(spanText: string, limit: number = WORD_SEARCH_LIMIT): SearchProbes {
  if (limit < 2) throw new RangeError("limit must be at least 2");
  const units = escapedUnits(spanText);
  const total = units.reduce((sum, u) => sum + u.width, 0);
  if (total <= limit) {
    return { head: escapeWordSearch(spanText), tail: null, headRaw: spanText, tailRaw: null };
  }
  let used = 0;
  let headRaw = "";
  for (const u of units) {
    if (used + u.width > limit) break;
    headRaw += u.raw;
    used += u.width;
  }
  used = 0;
  const tailParts: string[] = [];
  for (let i = units.length - 1; i >= 0; i--) {
    const u = units[i] as { raw: string; width: number };
    if (used + u.width > limit) break;
    tailParts.push(u.raw);
    used += u.width;
  }
  const tailRaw = tailParts.reverse().join("");
  return { head: escapeWordSearch(headRaw), tail: escapeWordSearch(tailRaw), headRaw, tailRaw };
}
